import {
	generateTrackingId,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { ActionAndOthers } from "client_legacy/ndaw/actions";
import {
	Fragment,
	ReactElement,
	RefObject,
	useMemo,
	useRef,
	useState
} from "react";
import styles from "./HubTemplateCard.module.less";
import { useCallbackWithoutArguments } from "client/Hooks";
import {
	actionComponentFactory,
	computeActionsToRender
} from "client/Components/ActionList/actionsRenderHelpers";
import {
	ContextMenu,
	DropOverActionList,
	IconButton,
	useContextMenu
} from "client/Components";
import classNames from "classnames";
import { HubThumbnail } from "../HubThumbnail/HubThumbnail";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { HubItemMediaKind, HubItemMediaPreviewViewModel } from "client/Api/Hub/common";

export type HubTemplateCardProps<TActionContext> = {
	name: string;
	isSelected: boolean;
	trackingId: string;
	onClick(): void;
	onDoubleClick(): void;
	kind?: HubItemKind;
	tooltip?: string;
	media?: HubItemMediaPreviewViewModel;
} & ({
	actions?: undefined;
} | {
	actions: readonly ActionAndOthers<TActionContext>[];
	actionContext: TActionContext;
	actionsTitle: string;
});

export const HubTemplateCard = <TActionContext, >(props: HubTemplateCardProps<TActionContext>): ReactElement => {
	const {
		kind,
		name,
		isSelected,
		trackingId,
		tooltip,
		media,
		onClick: onClickFromProps,
		onDoubleClick: onDoubleClickFromProps
	} = props;

	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

	const itemRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const menuButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);
	const onClick: () => void = useCallbackWithoutArguments(onClickFromProps);
	const onDoubleClick: () => void = useCallbackWithoutArguments(onDoubleClickFromProps);
	const [contextMenuHandler, contextMenuProps] = useContextMenu(itemRef);

	const actionsToRender = useMemo(
		(): readonly ActionAndOthers<TActionContext>[] | null => {
			if (!props.actions) {
				return null;
			}

			return computeActionsToRender(
				props.actions,
				props.actionContext
			);
		},
		[props]
	);

	const renderThumbnail = (): ReactElement | null => {
		if (!kind || !media) {
			return null;
		}

		return (
			<HubThumbnail
				hubItemKind={kind}
				media={
					media.mediaKind === HubItemMediaKind.Embed
						? { mediaKind: HubItemMediaKind.None }
						: media
				}
				title={name}
				mediaPlaceholderSize={media.mediaKind === HubItemMediaKind.Video
					? "medium"
					: "large"
				}
			/>
		);
	};

	const renderActions = (): ReactElement | null => {
		if (!props.actions
			|| !actionsToRender
			|| actionsToRender.length === 0
		) {
			return null;
		}

		return (
			<>
				{isMenuOpen && menuButtonRef.current && (
					<DropOverActionList
						trackingId={generateTrackingId(trackingId, "__drop-over")}
						target={menuButtonRef.current}
						targetAlignment="bl"
						contentAlignment="tl"
						onClose={(): void => setIsMenuOpen(false)}
						bubbleClick={false}
						bubbleContextMenu={false}
						menuTitle={name}
					>
						{actionsToRender.map((x) => (
							<Fragment key={x.name}>
								{actionComponentFactory(x, props.actionContext)}
							</Fragment>
						))}
					</DropOverActionList>
				)}
				{contextMenuProps && (
					<ContextMenu
						{...contextMenuProps}
						actions={actionsToRender}
						context={props.actionContext}
						trackingId={generateTrackingId(trackingId, "__context-menu")}
						title={props.actionsTitle}
					/>
				)}
			</>
		);
	};

	const hasMedia: boolean = isNotNil(media)
		&& media.mediaKind !== HubItemMediaKind.None;

	const shouldRenderMoreButton: boolean = isNotNil(actionsToRender)
		&& actionsToRender?.length > 0;

	return (
		<>
			<div
				{...setTrackingId(trackingId)}
				ref={itemRef}
				className={classNames(
					styles.root,
					{ [styles.root_selected]: isSelected }
				)}
				onClick={onClick}
				onDoubleClick={onDoubleClick}
				onContextMenu={contextMenuHandler}
			>
				<div className={styles.tools}>
					{shouldRenderMoreButton && (
						<IconButton
							trackingId="more"
							icon={{
								kind: "font",
								name: "ellipsis",
								color: hasMedia
									? "white"
									: undefined
							}}
							kind="transparent-colored"
							onClick={() => setIsMenuOpen(true)}
							ref={menuButtonRef}
							bubbleClick={false}
							shape="round"
							isActive={isMenuOpen}
						/>
					)}
				</div>
				<div
					className={classNames(
						styles.thumbnail,
						{ [styles.thumbnail_hasMedia]: hasMedia}
					)}
					title={tooltip}
				>
					{renderThumbnail()}
				</div>
				<div
					className={styles.title}
					title={tooltip}
				>
					<h4
						{...setTrackingId("name")}
					>
						{name}
					</h4>
				</div>
			</div>
			{renderActions()}
		</>
	);
};
HubTemplateCard.displayName = "HubTemplateCard";